import Decimal from 'decimal.js'

const DEFAULT_SCALE = 100
const DEFAULT_WEIGHT = 100

export const DietMealComponentPivot = (json: IDietMealComponentPivot): IDietMealComponentPivotModel => {
	const { net_weight, raw_weight, weight_after_processing, weight_unit, component_type, ...rest } = json

	return {
		...rest,
		componentType: component_type,
		netWeight: Number(net_weight),
		rawWeight: Number(raw_weight),
		weightAfterProcessing: Number(weight_after_processing),
		weightUnit: weight_unit,
	}
}

export const DietMealVariantPackagingItemMeal = (
	json: IDietMealPackagingAssignmentItemMeal,
): IDietMealPackagingAssignmentItemMealModel => {
	const { name, crm_semi_product_id, updated_at, created_at, ...rest } = json

	return {
		...rest,
		name: typeof name === 'string' ? name : LangContentToModel(name),
		crmSemiProductId: crm_semi_product_id,
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const DietMealVariantPackagingItem = (
	json: IDietMealPackagingAssignmentItem,
): IDietMealPackagingAssignmentItemModel => {
	const { model, model_id, model_type, updated_at, created_at, ...rest } = json

	return {
		...rest,
		model: DietMealVariantPackagingItemMeal(model),
		modelId: model_id,
		modelType: model_type,
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const DietMealVariantPackagingType = (
	json: IDietMealPackagingAssignmentPackagingType,
): IDietMealPackagingAssignmentPackagingTypeModel => {
	const { name, meal_kind, compartments_qty, ...rest } = json

	return {
		...rest,
		name: LangContentToModel(name),
		mealKind: meal_kind,
		compartmentsQty: compartments_qty,
	}
}

export const DietMealVariantPackaging = (json: IDietMealPackagingAssignment): IDietMealPackagingAssignmentModel => {
	const { meal_packaging_type_id, items, packaging_type, can_produce_separately, created_at, updated_at, ...rest } =
		json

	return {
		...rest,
		canProduceSeparately: can_produce_separately,
		items: items ? items.map(i => DietMealVariantPackagingItem(i)) : [],
		packagingType: DietMealVariantPackagingType(packaging_type),
		mealPackagingTypeId: meal_packaging_type_id,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const DietMealComponent = (json: IDietMealComponent): IDietMealComponentModel => {
	const {
		name,
		internal_name,
		warehouse_name,
		is_active,
		allergens,
		pivot,
		nutritional_parameters,
		standardization_factor,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
		allergens: allergens ? allergens.map(a => DietAllergen(a)) : [],
		nutritionalParameters: nutritional_parameters
			? nutritional_parameters.map(n => NutritionalParameterWithValue(n))
			: [],
		pivot: DietMealComponentPivot(pivot),
		standardizationFactor: standardization_factor,
	}
}

export const DietMealVariant = (json: IDietMealVariant): IDietMealVariantModel => {
	const {
		meal_id,
		meal_size_id,
		meal_size,
		preparation_time,
		is_active,
		meal_packaging_assignments,
		components,
		nutritional_parameters,
		updated_at,
		created_at,
		...rest
	} = json

	return {
		...rest,
		mealId: meal_id,
		mealSizeId: meal_size_id,
		mealSize: meal_size ? MealSize(meal_size) : null,
		preparationTime: preparation_time,
		isActive: is_active,
		packagings: meal_packaging_assignments ? meal_packaging_assignments.map(p => DietMealVariantPackaging(p)) : [],
		components: components ? components.map(c => DietMealComponent(c)) : [],
		nutritionalParameters: nutritional_parameters
			? nutritional_parameters.map(n => NutritionalParameterWithValue(n))
			: null,
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const DietMeal = (json: IDietMeal): IDietMealModel => {
	const {
		name,
		name_trans,
		short_name,
		internal_name,
		description,
		recipe,
		meal_type,
		meal_kind,
		meal_category,
		addon_category,
		meal_price_tier,
		min_order_qty,
		max_order_qty,
		serve_warm,
		serve_cold,
		preparation_difficulty,
		is_active,
		has_same_package_size,
		media,
		allergens,
		variants,
		components,
		food_production_stages,
		filter_tags,
		marketing_tags,
		dietary_tags,
		related_meals,
		updated_at,
		created_at,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		nameTrans: name_trans ?? null,
		shortName: LangContentToModel(short_name),
		internalName: internal_name,
		description: description ? LangContentToModel(description) : null,
		recipe: recipe ? LangContentToModel(recipe) : null,
		isActive: is_active,
		mealType: meal_type,
		mealKind: meal_kind,
		mealCategory: meal_category,
		addonCategory: addon_category,
		mealPriceTier: meal_price_tier,
		minOrderQty: min_order_qty,
		maxOrderQty: max_order_qty,
		serveWarm: serve_warm,
		serveCold: serve_cold,
		preparationDifficulty: preparation_difficulty,
		hasSamePackageSize: has_same_package_size,
		media: media ? media.map(m => Media(m)) : null,
		allergens: allergens ? allergens.map(a => DietAllergen(a)) : [],
		variants: variants ? variants.map(v => DietMealVariant(v)) : [],
		components: components ? components.map(c => DietMealComponent(c)) : [],
		foodProductionStages: food_production_stages ? food_production_stages.map(p => DietProductionStep(p)) : [],
		filterTags: filter_tags ? filter_tags.map(f => DietFilterTag(f)) : [],
		marketingTags: marketing_tags ? marketing_tags.map(m => DietMarketingTag(m)) : [],
		dietaryTags: dietary_tags ? dietary_tags.map(d => DietDietaryTag(d)) : [],
		relatedMeals: related_meals ? related_meals.map(r => DietMeal(r)) : [],
		updatedAt: updated_at ? new Date(updated_at) : null,
		createdAt: created_at ? new Date(created_at) : null,
	}
}

export const DietMealEditHistory = (json: IDietMealEditHistory): IDietMealEditHistoryModel => {
	const { date, ...rest } = json

	return {
		date: date ? new Date(date) : null,
		...rest,
	}
}

export const DietMealType = (json: IDietMealType): IDietMealTypeModel => {
	const { meal_type, meal_count, ...rest } = json

	return {
		mealType: meal_type,
		mealCount: meal_count,
		...rest,
	}
}

/**
 * Forms
 */
export const DietMealVariantPackagingItemToStepForm = (
	data: IDietMealPackagingAssignmentItemModel,
): IDietMealPackagingStep => {
	const { compartment, order, description, modelType, model } = data

	return {
		compartment,
		description,
		order,
		component: model.id,
		type: modelType,
	}
}

export const DietMealVariantPackagingToForm = (
	model: IDietMealPackagingAssignmentModel,
	index: number,
): IDietMealPackagingForm => {
	const { items, packagingType, canProduceSeparately, ...rest } = model

	const { compartmentsQty, type } = packagingType

	const mode = index > 0 ? EPackagingMode.ADDITIONAL : EPackagingMode.GENERAL

	return {
		...rest,
		name: mode === EPackagingMode.ADDITIONAL ? `Opakowanie ${index}` : undefined,
		mode,
		steps: items.map(item => DietMealVariantPackagingItemToStepForm(item)) ?? [],
		// @ts-expect-error template literal type mismatch
		type: `${type}-${compartmentsQty}`,
		size: packagingType.id,
		canProduceSeparately,
	}
}

export const DietMealVariantToForm = (model: IDietMealVariantModel): IDietMealVariantForm => {
	const { packagings, nutritionalParameters, ...rest } = model

	return {
		...rest,
		isDisabled: !model.isActive,
		nutritionalParameters: nutritionalParameters ?? [],
		packagings: packagings.map((packaging, index) => DietMealVariantPackagingToForm(packaging, index)),
	}
}

// TODO: Check is this model could have allergens
export const DietMealSemiProductDetailsToIngredient = (
	model: IDietSemiProductComponentModel,
): IDietMealIngredientForm => {
	const { id, name, nutritionalParameters, pivot } = model

	const { netWeight, rawWeight, weightAfterProcessing } = pivot

	const thermalProcessingCoefficient =
		netWeight > 0 ? Number(new Decimal(weightAfterProcessing).div(netWeight).times(100).toFixed(2)) : 100

	return {
		id,
		name,
		thermalProcessingCoefficient,
		group: null,
		type: EDietComponentType.SEMIPRODUCT,
		nutritions: nutritionalParameters,
		allergens: [],
		components: [],

		scale: DEFAULT_SCALE,
		scaleWeight: DEFAULT_WEIGHT,
		rawWeight: Number(rawWeight),
		netWeight: Number(netWeight),
		weightAfterProcessing: Number(weightAfterProcessing),
	}
}

export const DietMaterialModelToIngredientForm = (model: IDietMaterialModel): IDietMealIngredientForm => {
	const { id, name, allergens, nutritionalParameters, thermalProcessingCoefficient } = model

	return {
		id,
		name,
		allergens,
		thermalProcessingCoefficient,
		group: null,
		type: EDietComponentType.MATERIAL,
		nutritions: nutritionalParameters,

		scale: DEFAULT_SCALE,
		scaleWeight: DEFAULT_WEIGHT,
		rawWeight: DEFAULT_WEIGHT,
		netWeight: DEFAULT_WEIGHT,
		weightAfterProcessing: DEFAULT_WEIGHT,
	}
}

export const DietMealComponentsMaterialToIngredientForm = (
	model: IDietComponentsRawMaterialModel,
): IDietMealIngredientForm => {
	const { id, name, nutritionalParameters, thermalProcessingCoefficient } = model

	const _thermalProcessingCoefficient = thermalProcessingCoefficient ? Number(thermalProcessingCoefficient) : 100

	return {
		id,
		name,
		group: null,
		type: EDietComponentType.MATERIAL,
		allergens: [],
		nutritions: nutritionalParameters,

		scale: DEFAULT_SCALE,
		scaleWeight: DEFAULT_WEIGHT,
		rawWeight: DEFAULT_WEIGHT,
		netWeight: DEFAULT_WEIGHT,
		weightAfterProcessing: DEFAULT_WEIGHT,
		thermalProcessingCoefficient: _thermalProcessingCoefficient,
	}
}

export const DietComponentsSemiProductComponentToIngredientForm = (
	model: IDietComponentsSemiProductComponentModel,
): IDietMealIngredientForm => {
	const { id, name, nutritionalParameters, type, pivot } = model

	const { rawWeight, netWeight, weightAfterProcessing } = pivot

	const thermalProcessingCoefficient =
		netWeight > 0 ? Number(new Decimal(weightAfterProcessing).div(netWeight).times(100).toFixed(2)) : 100

	return {
		id,
		name,
		type,
		rawWeight,
		netWeight,
		weightAfterProcessing,
		thermalProcessingCoefficient,
		group: null,
		allergens: [],
		nutritions: nutritionalParameters,

		scale: DEFAULT_SCALE,
		scaleWeight: DEFAULT_WEIGHT,
	}
}

export const DietMealComponentsSemiProductToIngredientForm = (
	model: IDietComponentsSemiProductModel,
): IDietMealIngredientForm => {
	const { id, name, allergens, nutritionalParameters, components } = model

	const netWeight = components.reduce((acc, curr) => new Decimal(acc).plus(curr.pivot.netWeight).toNumber(), 0)
	const weightAfterProcessing = components.reduce(
		(acc, curr) => new Decimal(acc).plus(curr.pivot.weightAfterProcessing).toNumber(),
		0,
	)

	const thermalProcessingCoefficient =
		netWeight > 0 ? Number(new Decimal(weightAfterProcessing).div(netWeight).times(100).toFixed(2)) : 100

	return {
		id,
		name,
		allergens,
		netWeight,
		weightAfterProcessing,
		thermalProcessingCoefficient,
		group: null,
		type: EDietComponentType.SEMIPRODUCT,
		nutritions: nutritionalParameters,
		components: components.map(c => DietComponentsSemiProductComponentToIngredientForm(c)),

		scale: DEFAULT_SCALE,
		scaleWeight: DEFAULT_WEIGHT,
		rawWeight: DEFAULT_WEIGHT,
	}
}

export const DietMealComponentToIngredientForm = (model: IDietMealComponentModel): IDietMealIngredientForm => {
	const { id, name, type, allergens, nutritionalParameters } = model

	return {
		id,
		name,
		type,
		allergens,
		group: null,
		nutritions: nutritionalParameters,

		scale: DEFAULT_SCALE,
		scaleWeight: DEFAULT_WEIGHT,
		rawWeight: DEFAULT_WEIGHT,
		netWeight: DEFAULT_WEIGHT,
		weightAfterProcessing: DEFAULT_WEIGHT,
		thermalProcessingCoefficient: DEFAULT_WEIGHT,
	}
}

/**
 * Payloads
 */
export const DietMealModelByPortionToVariantComponentPayload = (
	ingridient: IDietMealIngredientForm,
): IDietMealComponentPayload => {
	const { id, rawWeight, netWeight, weightAfterProcessing, type, group } = ingridient

	return {
		id,
		type,
		group: group ?? null,
		raw_weight: rawWeight,
		net_weight: netWeight,
		weight_after_processing: weightAfterProcessing,
		weight_unit: 'g',
	}
}

export const DietMealModelByPortionToVariantPackagingItemPayload = (
	model: IDietMealPackagingStep,
): IDietMealVariantPackagingItemPayload | undefined => {
	const { type, order, compartment, description, component } = model

	if (!component || !type || !compartment) return undefined

	return {
		id: component,
		type,
		order,
		description,
		compartment,
	}
}

export const DietMealModelByPortionToVariantPackagingPayload = (
	model: IDietMealPackagingForm,
): IDietMealVariantPackagingPayload | undefined => {
	const { type, size, steps, canProduceSeparately } = model

	if (!type || !size) return undefined

	return {
		packaging_type_id: size,
		can_produce_separately: canProduceSeparately,
		items: steps
			.map(step => DietMealModelByPortionToVariantPackagingItemPayload(step))
			.filter(item => item !== undefined),
	}
}

export const DietMealModelByPortionToVariantPayload = (model: TDietMealsModelByPortion): IDietMealVariantPayload[] => {
	const variants: IDietMealVariantPayload[] = []

	Object.keys(model).forEach(key => {
		if (key === EMealSize.ALL) return

		const variant = model[key as EMealSharedPortion]

		const portion: IDietMealModelByPortionForm = model[key as EMealSize]

		const components: IDietMealComponentPayload[] = portion.ingridients.flatMap(ingredient => {
			if (!ingredient.group) return [DietMealModelByPortionToVariantComponentPayload(ingredient)]

			return ingredient.components?.map(i => DietMealModelByPortionToVariantComponentPayload(i)) ?? []
		})

		if (!variant.variant) return

		const packagings =
			components.length > 0
				? variant.variant.packagings
						.map(packaging => DietMealModelByPortionToVariantPackagingPayload(packaging))
						.filter(item => item !== undefined)
				: []

		variants.push({
			components,
			packagings,
			id: variant.variant.id,
			meal_size_id: variant.variant.mealSizeId as number,
			preparation_time: variant.variant.preparationTime,
			is_active: !variant.variant.isDisabled,
		})
	})

	return variants
}

const DietMealSharedToPayload = (
	event: IFormSubmitEvent<IDietMealFormData>,
	others: IDietMealPayloadMapperOptions,
): IDietMealSharedPayload => {
	const { nameForKitchen, mealType, mealPriceTier, minOrderQty, maxOrderQty, serveWarm, serveCold } = event.values

	const {
		hasSamePackageSize,
		productionSteps,
		modelByPortion,
		filterTags,
		marketingTags,
		type,
		addonCategory,
		relatedMeals,
	} = others

	const components = modelByPortion[EMealSize.ALL].ingridients.flatMap(ingridient => {
		if (!ingridient.group) return [DietMealModelByPortionToVariantComponentPayload(ingridient)]

		return ingridient.components?.map(s => DietMealModelByPortionToVariantComponentPayload(s)) ?? []
	})

	const mealKindBasedOnType = {
		[EMealDialogType.MEAL]: EMealKind.MEAL,
		[EMealDialogType.ADDON_MEAL]: EMealKind.MEAL,
		[EMealDialogType.MEALPREP]: EMealKind.MEALPREP,
		[EMealDialogType.ADDON_MEAL_PREP]: EMealKind.MEALPREP,
	}

	const categoryBasedOnType = {
		[EMealDialogType.MEAL]: EMealCategory.MEAL,
		[EMealDialogType.ADDON_MEAL]: EMealCategory.ADDON,
		[EMealDialogType.MEALPREP]: EMealCategory.MEAL,
		[EMealDialogType.ADDON_MEAL_PREP]: EMealCategory.ADDON,
	}

	const category = categoryBasedOnType[type]
	const meal_kind = mealKindBasedOnType[type]
	const addon_category = addonCategory === EAddonsCategoryTypes.SANDWICH ? addonCategory : null

	const related_meals: IDietMealRelatedMealPayload[] = relatedMeals.map(meal => ({
		id: meal.id,
		relation_type: EDietRelatedType.MEAL_TO_MEALPREP,
	}))

	return {
		meal_kind,
		components,
		addon_category,
		related_meals,
		internal_name: nameForKitchen,
		meal_type: mealType,
		meal_price_tier: mealPriceTier,
		min_order_qty: minOrderQty === 0 ? null : minOrderQty,
		max_order_qty: maxOrderQty,
		serve_warm: serveWarm,
		serve_cold: serveCold,
		has_same_package_size: hasSamePackageSize,
		is_active: true,
		food_production_stages: productionSteps ? productionSteps.map(step => DietProductionStepToPayload(step)) : [],
		variants: DietMealModelByPortionToVariantPayload(modelByPortion),
		filter_tags: filterTags.map(tag => tag.value),
		marketing_tags: marketingTags.map(tag => tag.value),
		meal_category: category,
	}
}

export const DietMealUpdateToPayload = (
	updated: IDietMealModel,
	event: IFormSubmitEvent<IDietMealFormData>,
	others: IDietMealPayloadMapperOptions,
): IDietMealUpdatePayload => {
	const { nameForClient, nameShortcut, description } = event.values

	const shared = DietMealSharedToPayload(event, others)

	const descriptionFromLang = description ? generateLangContentArray(description) : null

	const _description = updated.description ? LangContentToJson(updated.description, description) : descriptionFromLang

	return {
		...shared,
		id: updated.id,
		name: LangContentToJson(updated.name, nameForClient),
		short_name: LangContentToJson(updated.shortName, nameShortcut),
		description: _description,
	}
}

export const DietMealCreateToPayload = (
	event: IFormSubmitEvent<IDietMealFormData>,
	others: IDietMealPayloadMapperOptions,
): IDietMealCreatePayload => {
	const { nameForClient, nameShortcut, description } = event.values

	const shared = DietMealSharedToPayload(event, others)

	return {
		...shared,
		name: generateLangContentArray(nameForClient),
		short_name: generateLangContentArray(nameShortcut),
		description: generateLangContentArray(description),
	}
}

/**
 * Check and delete
 */

/** @deprecated */
export const dietMealVariantPackageToJSON = (payload: IMealPackagingTypeModel): IMealPackagingPivot => {
	const { id, pivot } = payload

	return {
		packaging_type_id: id,
		description: pivot?.description ?? '',
		description_compartment_1: pivot?.descriptionCompartment1 ?? '',
		description_compartment_2: pivot?.descriptionCompartment2 ?? '',
		description_compartment_3: pivot?.descriptionCompartment3 ?? '',
	}
}

/** @deprecated */
export const dietMealVariantToJSON = (obj: Partial<IDietMealVariantModel>): Partial<IDietMealVariant> => {
	const { mealId, mealSizeId, preparationTime, isActive, packagingType, components, ...rest } = obj
	return {
		meal_id: mealId,
		meal_size_id: mealSizeId,
		preparation_time: preparationTime,
		packaging: packagingType ? dietMealVariantPackageToJSON(packagingType) : null,
		is_active: isActive,
		components: components ? components.map(c => dietSemiProductComponentToJSON(c)) : [],
		...rest,
	}
}

/**
 * @deprecated
 * To Clean
 */
export const dietMealToJSON = (obj: Partial<IDietMealModel>): Partial<IDietMeal> => {
	const {
		shortName,
		internalName,
		mealType,
		mealKind,
		mealPriceTier,
		minOrderQty,
		maxOrderQty,
		serveWarm,
		serveCold,
		preparationDifficulty,
		isActive,
		hasSamePackageSize,
		createdAt,
		updatedAt,
		variants,
		components,
		foodProductionStages,
		filterTags,
		marketingTags,
		...rest
	} = obj
	return {
		short_name: shortName,
		internal_name: internalName,
		meal_type: mealType,
		meal_kind: mealKind,
		meal_price_tier: mealPriceTier,
		min_order_qty: minOrderQty,
		max_order_qty: maxOrderQty,
		serve_warm: serveWarm,
		serve_cold: serveCold,
		preparation_difficulty: preparationDifficulty,
		is_active: isActive,
		has_same_package_size: hasSamePackageSize,
		created_at: createdAt,
		updated_at: updatedAt,
		variants: variants ? variants.map(v => dietMealVariantToJSON(v) as IDietMealVariant) : [],
		components: components ? components.map(c => dietSemiProductComponentToJSON(c)) : [],
		food_production_stages: foodProductionStages ? foodProductionStages.map(p => dietProductionStepToJSON(p)) : [],
		filter_tags: filterTags ? filterTags.map(f => f.value) : [],
		marketing_tags: marketingTags ? marketingTags.map(m => m.value) : [],
		...rest,
	}
}

/**
 * @deprecated
 */
export const DietMaterialModelToIngredient = (model: IDietMaterialModel): IDietMealIngredientModel => {
	const { id, nutritionalParameters, thermalProcessingCoefficient } = model

	return {
		id,
		nutritions: nutritionalParameters,
		packageType: 0,
		group: null,
		type: EDietComponentType.MATERIAL,
		weight: 0,
		baseWeight: 0,

		scale: 100,
		processScale: thermalProcessingCoefficient,
		rawWeight: 0,
		netWeight: 0,
		weightAfterProcessing: 0,

		component: model,
		standardizationFactor: null,
		packageSizeForm: undefined,
		packageTypeForm: undefined,
	}
}

/**
 * @deprecated
 */
export const DietMealComponentToIngridient = (component: IDietMealComponentModel): IDietMealIngredientModel => {
	const { id, nutritionalParameters, pivot, type } = component

	const { netWeight, rawWeight, weightAfterProcessing, group } = pivot

	const processScale = numberToTwoDigitsFormat(
		Number(netWeight) > 0 ? (Number(weightAfterProcessing) / Number(netWeight)) * 100 : 100,
	)

	return {
		id,
		type,
		processScale,
		component,
		nutritions: nutritionalParameters,
		packageType: 0,
		group: group ?? null,
		weight: Number(weightAfterProcessing),
		baseWeight: Number(netWeight),

		scale: 100,
		rawWeight: Number(rawWeight),
		netWeight: Number(netWeight),
		weightAfterProcessing: Number(weightAfterProcessing),

		standardizationFactor: null,
		packageSizeForm: undefined,
		packageTypeForm: undefined,
	}
}

/**
 * @deprecated
 */
export const DietMealComponentsSemiProductToIngredient = (
	model: IDietComponentsSemiProductModel,
	group?: string,
): IDietMealIngredientModel => {
	const { id, nutritionalParameters, components } = model

	const netWeight = components.reduce((acc, curr) => acc + Number(curr.netWeight), 0)
	const weightAfterProcessing = components.reduce((acc, curr) => acc + Number(curr.weightAfterProcessing), 0)

	const processScale = numberToTwoDigitsFormat(netWeight > 0 ? (weightAfterProcessing / netWeight) * 100 : 100)

	return {
		id,
		processScale,
		netWeight,
		nutritions: nutritionalParameters,
		packageType: 0,
		group: group ?? null,
		type: EDietComponentType.SEMIPRODUCT,
		weight: 100,
		baseWeight: 100,

		scale: 100,
		rawWeight: 100,
		weightAfterProcessing: numberToTwoDigitsFormat((netWeight * processScale) / 100),

		component: model,
		standardizationFactor: null,
		packageSizeForm: undefined,
		packageTypeForm: undefined,
	}
}

/**
 * @deprecated
 */
export const DietMealComponentsMaterialToIngredient = (
	model: IDietComponentsRawMaterialModel,
	group?: string | null,
): IDietMealIngredientModel => {
	const { id, nutritionalParameters, thermalProcessingCoefficient } = model

	return {
		id,
		nutritions: nutritionalParameters,
		packageType: 0,
		group: group ?? null,
		type: EDietComponentType.MATERIAL,
		weight: 0,
		baseWeight: 0,

		scale: Number(100),
		processScale: thermalProcessingCoefficient,
		rawWeight: 0,
		netWeight: 0,
		weightAfterProcessing: 0,

		component: model,
		standardizationFactor: null,
		packageSizeForm: undefined,
		packageTypeForm: undefined,
	}
}
